<template>
  <CRow>
    <CCol col="12">
      <CCard>
        <CCardHeader>
          <CIcon name="cil-user"/>
          <h5 class="d-inline ml-2">{{ this.first_name }}</h5>
          <div class="card-header-actions">
            <CButton
              v-if="user && isSuperAdmin"
              color="primary"
              class="mr-2"
              :to="{ name: 'Edit User', params: { id: user.id } }"
            >
              <CIcon name="cil-pencil" class="align-bottom" /> Edit User
            </CButton>
            <CButton
              v-if="hasCourierProfile"
              color="info"
              class="mr-2"
              :to="{ name: 'Edit Courier', params: { id: user.id } }"
            >
              <CIcon name="cil-bike" class="align-bottom" /> View Courier
            </CButton>
            <CButton color="dark" :to="this.prevRoute">
              <CIcon name="cil-arrow-thick-left" class="align-bottom" /> Back
            </CButton>
          </div>
        </CCardHeader>

        <CCardBody v-if="loading || !user">
          <CSpinner color="primary" />
        </CCardBody>

        <CCardBody v-else>
          <CMedia
            :aside-image-props="{
              width: 128,
              height: 128,
              src: user.image_icon || user.gravatar,
              shape: 'rounded-circle',
            }"
          >
            <CRow class="ml-2">
              <!-- Orders -->
              <CCol class="widget" col="12" sm="6" lg="3">
                <CWidgetIcon
                  header="Orders"
                  :text="String(user.orders_count)"
                  color="gradient-dark"
                  :icon-padding="false"
                >
                  <CIcon name="cil-dinner" class="" width="24" />
                  <template #footer>
                    <CCardFooter class="px-3 py-2">
                      <CLink
                        v-if="typeof user.id !== 'undefined'"
                        :to="{name: 'UserOrderList', params: { user_id: user.id }}"
                        class="font-sm text-muted d-flex justify-content-between"
                      >
                        View Orders
                        <CIcon name="cil-arrow-right" width="16"/>
                      </CLink>
                    </CCardFooter>
                  </template>
                </CWidgetIcon>
              </CCol>

              <!-- Reviews -->
              <CCol class="widget" col="12" sm="6" lg="3">
                <CWidgetIcon
                  header="Reviews"
                  :text="String(user.reviews_count)"
                  color="gradient-success"
                  :icon-padding="false"
                >
                  <CIcon name="cil-star" class="" width="24" />
                  <template #footer>
                    <CCardFooter class="px-3 py-2">
                      <CLink
                        v-if="typeof user.id !== 'undefined'"
                        :to="{ name: 'UserReviews', params: { user_id: user.id } }"
                        class="font-sm text-muted d-flex justify-content-between"
                      >
                        View Reviews
                        <CIcon name="cil-arrow-right" width="16"/>
                      </CLink>
                    </CCardFooter>
                  </template>
                </CWidgetIcon>
              </CCol>

              <!-- Points -->
              <CCol class="widget" col="12" sm="6" lg="3">
                <CWidgetIcon
                  header="Points"
                  :text="formattedUserPoints"
                  color="gradient-primary"
                  :icon-padding="false"
                >
                  <CIcon name="cil-list-numbered" class="" width="24" />
                  <template #footer v-if="isSuperAdmin && typeof user.id !== 'undefined'">
                    <CCardFooter class="px-3 py-2">
                      <CLink
                        :to="{ name: 'Edit User', params: { id: user.id } }"
                        class="font-sm text-muted d-flex justify-content-between"
                      >
                        Edit User
                        <CIcon name="cil-arrow-right" width="16"/>
                      </CLink>
                    </CCardFooter>
                  </template>
                </CWidgetIcon>
              </CCol>

              <!-- Vouchers -->
              <CCol v-if="isSuperAdmin" class="widget" col="12" sm="6" lg="3">
                <CWidgetIcon
                  header="Vouchers"
                  :text="String(user.vouchers_count || 0)"
                  color="gradient-danger"
                  :icon-padding="false"
                >
                  <CIcon name="cil-tags" class="" width="24" />
                  <template #footer>
                    <CCardFooter class="px-3 py-2">
                      <CLink
                        v-if="typeof user.id !== 'undefined'"
                        :to="{ name: 'Voucher List', query: { user_id: user.id, user_name: user.first_name } }"
                        class="font-sm text-muted d-flex justify-content-between"
                      >
                        View Vouchers
                        <CIcon name="cil-arrow-right" width="16"/>
                      </CLink>
                    </CCardFooter>
                  </template>
                </CWidgetIcon>
              </CCol>

              <!-- Devices -->
              <CCol class="widget" col="12" sm="6" lg="3">
                <CWidgetIcon
                  header="Devices"
                  :text="String(user.usersdevices_count || 0)"
                  color="gradient-info"
                  :icon-padding="false"
                >
                  <CIcon name="cil-mobile" class="" width="24" />
                  <template #footer>
                    <CCardFooter class="px-3 py-2">
                      <a v-smooth-scroll href="#user-devices" class="font-sm text-muted d-flex justify-content-between">
                        View Devices
                        <CIcon name="cil-arrow-right" width="16"/>
                      </a>
                    </CCardFooter>
                  </template>
                </CWidgetIcon>
              </CCol>

              <!-- Addresses -->
              <CCol class="widget" col="12" sm="6" lg="3">
                <CWidgetIcon
                  header="Addresses"
                  :text="String(user.addresses.length)"
                  color="gradient-warning"
                  :icon-padding="false"
                >
                  <CIcon name="cil-location-pin" class="" width="24" />
                  <template #footer>
                    <CCardFooter class="px-3 py-2">
                      <a v-smooth-scroll href="#user-addresses" class="font-sm text-muted d-flex justify-content-between">
                        View Addresses
                        <CIcon name="cil-arrow-right" width="16"/>
                      </a>
                    </CCardFooter>
                  </template>
                </CWidgetIcon>
              </CCol>
            </CRow>
          </CMedia>

          <h5 class="my-3">User Details</h5>

          <div class="d-inline-flex flex-wrap w-100">
            <div class="item-group">
              <label>Full Name:</label>
              <span>{{ user.first_name }}</span>
            </div>
            <div class="item-group">
              <label>ID#:</label>
              <span>{{ user.id }}</span>
            </div>
            <div class="item-group">
              <label>Points:</label>
              <span>{{ formattedUserPoints }}</span>
            </div>

            <div class="item-group">
              <label>Language:</label>
              <CIcon v-if="user.lang == 'en'" class="flag" :height="14" :content="$options.flagSet['cifGb']" />
              <CIcon v-else class="flag" :height="14" :content="$options.flagSet['cif' + user.lang.charAt(0).toUpperCase() + user.lang.slice(1).toLowerCase()]" />
              <span class="ml-2">{{ user.lang.toUpperCase() }}</span>
            </div>
            <div class="item-group">
              <label>Country:</label>
              <template v-if="user.country">
                <CIcon class="flag" :height="14" :content="$options.flagSet['cif' + user.country.charAt(0).toUpperCase() + user.country.slice(1).toLowerCase()]" />
                <span class="ml-2">{{ user.country.toUpperCase() }}</span>
              </template>
              <span v-else>-</span>
            </div>

            <div class="item-group">
              <label>User Type:</label>
              <span>{{ user.usertype }}</span>
            </div>
            <div class="item-group">
              <label>Type:</label>
              <span>{{ user.type }}</span>
            </div>

            <div class="item-group">
              <label>Mobile:</label>
              <span>{{ user.mobile }}</span>
            </div>
            <div class="item-group">
              <label>2FA Mobile:</label>
              <span>{{ user.tfamobile }}</span>
            </div>
            <div class="item-group">
              <label>Gender:</label>
              <span class="gender">{{ user.gender == 1 ? "♂" : "♀" }}</span>
            </div>

            <div class="item-group">
              <label>Email:</label>
              <span>{{ user.email }}</span>
            </div>
            <div class="item-group">
              <label>Email Verified At:</label>
              <span>{{ user.email_verified_at }}</span>
            </div>

            <div class="item-group">
              <label>Email Subscribe:</label>
              <CBadge :color="isEmailSubscribed ? 'success' : 'secondary'">
                {{ isEmailSubscribed ? "YES" : "NO" }}
              </CBadge>
            </div>
            <div class="item-group">
              <label>Internal Marketing Block:</label>
              <CBadge :color="isMarketingBlocked ? 'danger' : 'success'">
                {{ isMarketingBlocked ? "YES" : "NO" }}
              </CBadge>
              <span v-if="isMarketingBlocked" class="ml-2">{{ user.email_marketing_blocked_at }}</span>
            </div>
            <div class="item-group">
              <label>Effective Marketing Status:</label>
              <CBadge :color="effectiveMarketingStatus ? 'success' : 'secondary'">
                {{ effectiveMarketingStatus ? "Subscribed" : "Blocked / Unsubscribed" }}
              </CBadge>
            </div>
            <div class="item-group">
              <label>Facebook ID#:</label>
              <span>{{ user.fb_id }}</span>
            </div>

            <div class="item-group">
              <label>Google ID#:</label>
              <span>{{ user.google_id }}</span>
            </div>
            <div class="item-group">
              <label>Apple ID#:</label>
              <span>{{ user.apple_id }}</span>
            </div>

            <div class="item-group">
              <label>IP:</label>
              <span>{{ user.ip }}</span>
            </div>
            <div class="item-group">
              <label>Last Login:</label>
              <span>{{ user.last_login }}</span>
            </div>

            <div class="item-group">
              <label>Created At:</label>
              <span>{{ user.created_at }}</span>
            </div>
            <div class="item-group">
              <label>Updated At:</label>
              <span>{{ user.updated_at }}</span>
            </div>

            <div class="item-group">
              <label>Address:</label>
              <span>{{ user.address }}</span>
            </div>
            <div class="item-group">
              <label>City:</label>
              <span>{{ user.city }}</span>
            </div>

            <div class="item-group">
              <label>Postal Code:</label>
              <span>{{ user.postal_code }}</span>
            </div>
          </div>

          <CCard id="user-devices" class="mt-3">
            <CCardHeader>
              <CIcon name="cil-mobile" class="align-bottom" />
              <strong> User Devices </strong>
              <small>({{ user.usersdevices_count || 0 }})</small>
            </CCardHeader>
            <CCardBody>
              <CListGroup accent>
                <CListGroupItem
                  v-for="device of userDevices"
                  :key="device.id"
                  accent="info"
                  class="device-list-item"
                >
                  <div class="device-primary-row">
                    <div>
                      <strong>{{ formatDeviceTitle(device) }}</strong>
                      <span v-if="formatDeviceSecondary(device)" class="text-muted d-block">
                        {{ formatDeviceSecondary(device) }}
                      </span>
                    </div>
                    <CBadge :color="device.is_notify ? 'success' : 'secondary'">
                      {{ device.is_notify ? 'Notify On' : 'Notify Off' }}
                    </CBadge>
                  </div>

                  <div class="device-meta">
                    <div><strong>App:</strong> {{ formatDeviceApp(device) }}</div>
                    <div><strong>Device ID:</strong> {{ maskedDeviceId(device.device_id) }}</div>
                    <div><strong>Last Active:</strong> {{ formatDeviceSeenAt(device) }}</div>
                    <div v-if="formatDeviceLocation(device)"><strong>Location:</strong> {{ formatDeviceLocation(device) }}</div>
                    <div v-if="device.timezone"><strong>Timezone:</strong> {{ device.timezone }}</div>
                    <div v-if="device.country"><strong>Country:</strong> {{ device.country }}</div>
                    <div v-if="device.app_versions"><strong>App Version:</strong> {{ device.app_versions }}</div>
                  </div>
                </CListGroupItem>

                <CListGroupItem v-if="!userDevices.length" accent="secondary">
                  No linked devices
                </CListGroupItem>
              </CListGroup>
            </CCardBody>
          </CCard>

          <CCard id="user-crm-messages" class="mt-3">
            <CCardHeader>
              <CIcon name="cil-chat-bubble" class="align-bottom" />
              <strong> CRM Messages </strong>
            </CCardHeader>
            <CCardBody>
              <CrmRecipientMessages
                v-if="user"
                :url="userCrmMessagesUrl"
              />
            </CCardBody>
          </CCard>


          <CCard id="user-addresses" class="mt-3">
            <CCardHeader>
              <CIcon name="cil-location-pin" class="align-bottom" />
              <strong> Addresses </strong>
              <small>({{ user.addresses.length }})</small>
            </CCardHeader>
            <CCardBody>
              <CListGroup accent>
                <CListGroupItem v-for="address of user.addresses" :key="address.id" accent="warning">
                  <strong>{{ address.maPostalcode }}</strong>,
                  {{ address.maCity }}<br />
                  {{ address.maAddress }}
                </CListGroupItem>
              </CListGroup>
            </CCardBody>
          </CCard>

        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import { flagSet } from '@coreui/icons'
import CrmRecipientMessages from '@/components/CrmRecipientMessages'

export default {
  name: 'User',
  flagSet,
  components: {
    CrmRecipientMessages,
  },

  data () {
    return {
      user: null,
      courierProfile: null,
      loading: true,

      // Get previous page
      prevRoute: null,
    }
  },

  // Get previous page
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  async created () {
    await this.loadUser()
  },

  computed: {
    itemId () { return this.$route.params.id },
    first_name () { return this.user ? this.user.first_name : '' },
    userDevices () { return Array.isArray(this.user?.usersdevices) ? this.user.usersdevices : [] },
    userCrmMessagesUrl () { return this.$backend.USERS.CRM_MESSAGES.replace('{id}', this.itemId) },
    userPoints () {
      const points = Number(this.user?.point?.points)
      return Number.isFinite(points) ? points : 0
    },
    formattedUserPoints () {
      return new Intl.NumberFormat('nl-BE', {
        maximumFractionDigits: 2,
      }).format(this.userPoints)
    },
    isEmailSubscribed () {
      return this.user?.email_subscribe == true || this.user?.email_subscribe === 1
    },
    isMarketingBlocked () {
      return !!this.user?.email_marketing_blocked_at
    },
    effectiveMarketingStatus () {
      return this.isEmailSubscribed && !this.isMarketingBlocked
    },
    hasCourierProfile () { return !!this.courierProfile }
  },

  methods: {
    async loadUser() {
      await this.$axios
        .get(this.$backend.USERS.VIEW.replace('{id}', this.itemId))
        .then(async (response) => {
          this.user = response.data
          await this.loadCourierProfile()
        })
        .catch(() => this.$router.push({ name: 'All Users' }))
        .finally(() => this.loading = false)
    },
    async loadCourierProfile() {
      if (!this.$backend?.COURIER?.COURIERS?.SHOW) {
        return
      }

      await this.$axios
        .get(this.$backend.COURIER.COURIERS.SHOW.replace('{id}', this.itemId))
        .then((response) => this.courierProfile = response.data)
        .catch(() => this.courierProfile = null)
    },
    maskedDeviceId(deviceId) {
      if (!deviceId) {
        return '-'
      }

      if (deviceId.length <= 8) {
        return deviceId
      }

      return `${deviceId.slice(0, 4)}...${deviceId.slice(-4)}`
    },
    formatDeviceTitle(device) {
      return device.device_model || device.device_type || `Device #${device.id}`
    },
    formatDeviceSecondary(device) {
      return [device.manufacturer, device.device_os, device.device_type]
        .filter(Boolean)
        .join(' | ')
    },
    formatDeviceApp(device) {
      if (!device.app) {
        return 'Unknown app'
      }

      return [device.app.provider, device.app.domain || device.app.app_id]
        .filter(Boolean)
        .join(' - ')
    },
    formatDeviceSeenAt(device) {
      return device.last_active || device.updated_at || '-'
    },
    formatDeviceLocation(device) {
      return device.location || ''
    }
  }
}
</script>

<style>
  .widget .card, .widget .card-footer {
    background-color: #f8f8ff;
  }
  .widget .text-value {
    font-size: 1rem;
  }
  .widget .text-muted {
    font-size: 1.5rem;
  }
</style>

<style scoped>
.item-group {
  width: 50%;
  display: inline-block;
  padding: 0.75rem;
  vertical-align: top;
  border-top: 1px solid;
  border-top-color: #d8dbe0;
}
.item-group:nth-child(4n+1), .item-group:nth-child(4n+2) {
  background-color: #f2f2f2;
}
.item-group label {
  font-weight: bolder;
  margin-bottom: 0;
  width: 10rem;
}
.item-group .gender {
  font-family: arial unicode ms,lucida sans unicode,sans-serif;
  font-size: 1rem;
  text-shadow: 1px 0 1px #ccc, 0 1px 1px #eee, 2px 1px 1px #ccc;
}

.device-list-item + .device-list-item {
  margin-top: 0.5rem;
}

.device-primary-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0.75rem;
}

.device-meta {
  margin-top: 0.5rem;
}
</style>
